import { Link } from "react-router-dom";
import { brandPrimitives } from "@maeiz/tokens";
import { DocPage, Section, Demo } from "../components/DocChrome";

export function OverviewPage() {
    return (
        <DocPage
            title="Overview"
            description="One unified MAEIZ Workspace Design System for Owner, Tenant, Rider, and Customer. Landing page is out of scope."
        >
            <Section title="Brand locks (immutable)">
                <div className="flex flex-wrap gap-4">
                    <Swatch name="Royal blue" hex={brandPrimitives.blue.DEFAULT} />
                    <Swatch name="Authority gold" hex={brandPrimitives.gold.DEFAULT} />
                </div>
                <p>
                    Components consume semantic tokens from <code>@maeiz/tokens</code>. Panels
                    import components from <code>@maeiz/ui</code>. This app documents and demos the
                    system — it is not a fifth product panel.
                </p>
            </Section>

            <Section title="Architecture">
                <Demo title="Consumption model">
                    <pre className="overflow-x-auto rounded-mz-md bg-neutral-ink p-4 text-xs text-gold-light">
                        {`apps/design-system  → docs + live demos
packages/tokens      → primitive → semantic → component tokens
packages/ui          → canonical React components
apps/{owner,tenant,rider,customer} → consume tokens + ui`}
                    </pre>
                </Demo>
            </Section>

            <Section title="Coverage">
                <ul className="list-disc space-y-1 ps-5">
                    <li>
                        <Link className="text-primary underline" to="/foundations/principles">
                            Foundations
                        </Link>{" "}
                        — principles, tokens, type, layout, motion
                    </li>
                    <li>
                        <Link className="text-primary underline" to="/components/buttons">
                            Components
                        </Link>{" "}
                        — states, controls, data, overlays, feedback
                    </li>
                    <li>
                        <Link className="text-primary underline" to="/patterns/pages">
                            Patterns
                        </Link>{" "}
                        — page archetypes, a11y, writing, permissions
                    </li>
                    <li>
                        <Link className="text-primary underline" to="/governance/inventory">
                            Governance
                        </Link>{" "}
                        — inventory, migration, audit
                    </li>
                </ul>
            </Section>

            <Section title="Changelog">
                <ul className="list-disc space-y-1 ps-5">
                    <li>
                        <strong>2026-10-02</strong> — Initial Workspace DS:{" "}
                        <code>@maeiz/tokens</code>, living docs app, gold unified to{" "}
                        <code>#C9A736</code>, gap components in <code>@maeiz/ui</code>.
                    </li>
                </ul>
            </Section>
        </DocPage>
    );
}

function Swatch({ name, hex }: { name: string; hex: string }) {
    return (
        <div className="flex items-center gap-3 rounded-mz-lg border border-neutral-border bg-white p-3 dark:border-white/10 dark:bg-neutral-navy">
            <span
                className="h-12 w-12 rounded-mz-md shadow-mz-sm"
                style={{ backgroundColor: hex }}
            />
            <div>
                <p className="font-medium text-neutral-dark dark:text-white">{name}</p>
                <p className="font-mono text-xs text-neutral-secondary">{hex}</p>
            </div>
        </div>
    );
}
